import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Container, Card, Spinner, Button, Modal, Form, Image } from 'react-bootstrap';
import { InfoCircle, FileText, Clipboard, ClipboardCheck, ArrowLeft } from 'react-bootstrap-icons';
import useCollections, { CollectionModal } from '../../hooks/useCollections';
import { openCapacitorSite } from '../../utils/browserUtils';


const formatTableName = (tableName) => {
  return tableName
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ');
};

/**
 * Get the appropriate button name based on the table
 * This maps table names to their document button labels
 */
const getButtonNameForTable = (table) => {
  const buttonNameMap = {
    // Congressional
    'congressional_bills': 'Bill',
    'congressional_bills_enrolled': 'Bill',
    'public_and_private_laws': 'Law',
    'congressional_reports': 'Report',
    'congressional_records': 'Record',
    'gao_reports': 'Report',
    
    // Executive
    'white_house_briefings': 'Briefing',
    'white_house_disclosures': 'Disclosure',
    'presidential_actions': 'Document',
    'executive_orders': 'Order',
    'presidential_memoranda': 'Memorandum',
    'presidential_proclamations': 'Proclamation',
    
    // Judicial
    'supreme_court_opinions': 'Opinion',
    'supreme_court_orders': 'Order',
    'supreme_court_arguments': 'Argument',
    
    // Agencies
    'recent_awards': 'Award',
    'doge_savings': 'Document',
    'federal_register': 'Document',
    'sec_filings': 'Filing',
    'fda_approvals': 'Approval',
    'epa_documents': 'Document',
    'dot_documents': 'Document',
    'dod_contracts': 'Contract',
    'dod_releases': 'Release',
    
    // Committee materials
    'house_committee_materials': 'Document',
    'senate_committee_materials': 'Document',
  };

  return buttonNameMap[table] || 'Document';
};

// ─── Share links ──────────────────────────────────────────────────────────────
//
// A shared link has to reopen this same page, so it is built the way the page
// is looked up (see useFeedItemDetail), not always from the row's id:
//
//   all_member_votes   /{roll_call_number}?date={day}
//                      The page finds a vote by roll-call number, not by the
//                      row's own id; the date keeps it apart from the same
//                      number in the other session.
//   bills              /{package_id}, such as BILLS-119hr10473ih
//                      The GovInfo id this row was printed under, which is also
//                      how the member page links bills.
//   everything else    /{id}, as before
const SHARE_BASE = 'https://news.govglance.org';
const BILL_TABLES = ['congressional_bills', 'congressional_bills_enrolled', 'public_and_private_laws'];

const sharePathFor = (item, schema, table) => {
  const base = `/feed/${schema}/${table}`;

  if (table === 'all_member_votes' && item?.roll_call_number) {
    const day = String(item.created_at || '').slice(0, 10);
    const dated = /^\d{4}-\d{2}-\d{2}$/.test(day) ? `?date=${day}` : '';
    return `${base}/${encodeURIComponent(item.roll_call_number)}${dated}`;
  }

  if (BILL_TABLES.includes(table) && item?.package_id) {
    return `${base}/${encodeURIComponent(item.package_id)}`;
  }

  return `${base}/${encodeURIComponent(item?.id ?? item?.roll_call_number ?? '')}`;
};

export const FeedItemDetailContent = ({ 
  item, 
  allVotes = [],
  membersData = [],
  loadingMembers = false,
  table, 
  schema, 
  onBack,
  buttonName: buttonNameProp
}) => {
  const navigate = useNavigate();
  
  // Use passed buttonName or derive from table
  const buttonName = buttonNameProp || getButtonNameForTable(table);
  
  // Use the collection hook
  const {
    isModalOpen,
    currentItem,
    saveSuccess,
    note,
    setNote,
    openCollectionModal,
    closeModal,
    handleSave,
    isStorageAvailable,
    CollectionButton
  } = useCollections();
  
  // Share modal states
  const [shareModalIsOpen, setShareModalIsOpen] = useState(false);
  const [shareLink, setShareLink] = useState('');
  const [linkCopied, setLinkCopied] = useState(false);

  // Format date function
  const formatDate = (inputDate) => {
    if (!inputDate) return '';
    
    // If it's already in M-D-YYYY format, return as-is
    if (typeof inputDate === 'string' && /^\d{1,2}-\d{1,2}-\d{4}$/.test(inputDate)) {
      return inputDate;
    }
    
    try {
      const date = new Date(inputDate);
      if (isNaN(date.getTime())) return '';
      
      const utcDate = new Date(date.getTime() + date.getTimezoneOffset() * 60000);
      const day = utcDate.getDate();
      const month = utcDate.getMonth() + 1;
      const year = utcDate.getFullYear();
      return `${month}-${day}-${year}`;
    } catch (error) {
      return '';
    }
  };

  // Get most recent action date for congressional bills
  const getMostRecentActionDate = (actions) => {
    if (!actions || actions.length === 0) return null;

    let mostRecentAction = null;
    for (let action of actions) {
      if (action.action_description) {
        if (!mostRecentAction || new Date(action.action_date) > new Date(mostRecentAction.action_date)) {
          mostRecentAction = action;
        }
      }
    }

    if (!mostRecentAction) return null;

    const actionDate = new Date(mostRecentAction.action_date + 'T00:00:00Z');
    actionDate.setMinutes(actionDate.getMinutes() + actionDate.getTimezoneOffset());
    
    const formattedDate = `${actionDate.getMonth() + 1}-${actionDate.getDate()}-${actionDate.getFullYear()}`;
    return formattedDate;
  };

  // Handle member click - navigate to member detail page
  const handleMemberClick = (matchedMember) => {
    const firstName = matchedMember.first_name || '';
    const lastName = matchedMember.last_name || '';
    const fullName = `${firstName} ${lastName}`;
    const imageSource = matchedMember.depication?.imageUrl || '';
    const latestTerm = matchedMember.terms?.[matchedMember.terms.length - 1] || {};
    const chamber = latestTerm.chamber || '';
    
    // Determine the correct route based on chamber
    let route;
    if (chamber === 'Senate') {
      route = `/us/senator/${matchedMember.bio_guide_id}`;
    } else {
      route = `/us/representative/${matchedMember.bio_guide_id}`;
    }
    
    navigate(route, {
      state: {
        name: fullName,
        webURL: matchedMember.official_website_url || '',
        imageSource: imageSource,
        chamber: chamber,
        startYear: latestTerm.startYear || '',
        stateName: latestTerm.stateName || '',
        party: matchedMember.party_history || [],
        district: matchedMember.district || '',
        address: matchedMember.address_information || {},
      },
    });
  };

  // Render sponsors and cosponsors for congressional bills
  const renderSponsorsAndCosponsors = () => {
    if (!item?.members || item.members.length === 0) return null;
    if (loadingMembers) {
      return (
        <div className="mt-3">
          <Card.Title id="billText">Sponsors and Cosponsors:</Card.Title>
          <Spinner id='spinner' animation="border" variant="light" size="sm" />
        </div>
      );
    }

    return (
      <div className="mt-3">
        <Card.Title id="billText">Sponsors and Cosponsors:</Card.Title>
        <div className="sponsors-list">
          {item.members.map((member, index) => {
            const matchedMember = membersData.find(
              (m) => m.bio_guide_id === member.bioGuideId
            );

            if (!matchedMember) return null;

            const imageUrl = matchedMember.depication?.imageUrl || '';
            const firstName = matchedMember.first_name || '';
            const lastName = matchedMember.last_name || '';
            const role = member.role;
            const party = member.party;
            const state = member.state;

            return (
              <div
                key={index}
                className="sponsorItem"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '6px',
                  gap: '12px',
                  cursor: 'pointer'
                }}
                onClick={() => handleMemberClick(matchedMember)}
              >
                <div id="profileImageSponsors">
                  <Image
                    id="profileImage-image"
                    src={imageUrl}
                    alt={`${firstName} ${lastName}`}
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      objectFit: 'cover'
                    }}
                  />
                </div>
                <p className="sponsor-text">
                  <strong>{firstName} {lastName} - {role} ({party}-{state})</strong>
                </p>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Render member votes for all_member_votes table
  const renderMemberVotes = () => {
    if (table !== 'all_member_votes' || allVotes.length === 0) return null;
    
    if (loadingMembers) {
      return (
        <div className="mt-3">
          <Card.Title id="billText">Votes by Members:</Card.Title>
          <Spinner id='spinner' animation="border" variant="light" size="sm" />
        </div>
      );
    }

    return (
      <div className="mt-3">
        <Card.Title id="billText">Votes by Members:</Card.Title>
        <div className="votes-list">
          {allVotes.map((voteItem, voteIndex) => {
            const matchedMember = membersData.find(
              (m) => m.bio_guide_id === voteItem.bio_guide_id
            );

            if (!matchedMember) return null;

            const imageUrl = matchedMember.depication?.imageUrl || '';
            const firstName = matchedMember.first_name || '';
            const lastName = matchedMember.last_name || '';
            const vote = voteItem.vote;
            const party = matchedMember.party_history?.[0]?.partyAbbreviation || 'Unknown';
            const state = matchedMember.state || 'Unknown';

            return (
              <div
                key={voteIndex}
                className="voteItem"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: '6px',
                  gap: '12px',
                  cursor: 'pointer'
                }}
                onClick={() => handleMemberClick(matchedMember)}
              >
                <div id="profileImageSponsors">
                  <Image
                    id="profileImage-image"
                    src={imageUrl}
                    alt={`${firstName} ${lastName}`}
                    style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      objectFit: 'cover'
                    }}
                  />
                </div>
                <p className="vote-text">
                  <strong>{firstName} {lastName} ({party}-{state})</strong>&nbsp; Vote: {vote}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  // Handle adding to collection - prepare item data for the hook
  const handleAddToCollection = () => {
    if (!item) return;
    
    const titleToDisplay = table === 'x_account_1856751787644260354_posts' 
      ? item.description 
      : (item.title || item.status_title);
    
    const collectionItem = {
      title: titleToDisplay,
      url: item.url,
      docLink: item.document_link || item.download?.pdfLink || '',
      date: item.created_at,
      feedName: formatTableName(table),
      branch: '',
      topic: ''
    };
    
    openCollectionModal(collectionItem);
  };

  // Handle share click. The link reopens this same page -- see sharePathFor.
  const handleShareClick = () => {
    setShareLink(`${SHARE_BASE}${sharePathFor(item, schema, table)}`);
    setShareModalIsOpen(true);
    setLinkCopied(false);
  };

  // Copy link to clipboard
  const handleCopyLink = () => {
    navigator.clipboard.writeText(shareLink).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 3000);
    });
  };

  if (!item) {
    return (
      <div className="detail-loading-container">
        <Spinner id='spinner' animation="grow" variant="light" />
      </div>
    );
  }

  const { 
    title, 
    status_title, 
    url, 
    document_link, 
    created_at, 
    description, 
    actions, 
    committees,
    award_amount,
    amount,
    annual_lease,
    saved_amount,
    saving_type,
    download
  } = item || {};

  const titleToDisplay = table === 'x_account_1856751787644260354_posts' ? description : title || status_title;
  const link = url;
  // Match FeedItem logic - check document_link first, then download.pdfLink
  const docLink = document_link || download?.pdfLink || null;
  const date = created_at ? formatDate(created_at) : '';
  const awardAmount = award_amount || amount || annual_lease || '';
  const savedAmount = saved_amount || '';
  const type = saving_type;

  return (
    <div className="detail-page-container">
      <div className="detail-content-wrapper">
        <Container id="feedContainerNormalView">

          {/* Title */}
          <Card.Title
            id="billTextMore"
            onClick={() => {
              const linkToOpen = docLink || link;
              if (linkToOpen) openCapacitorSite(linkToOpen);
            }}
            style={{ cursor: (docLink || link) ? 'pointer' : 'default' }}
          >
            {titleToDisplay}
          </Card.Title>

          {/* Date */}
          <Card.Body id="billBodyText">
            {table === 'congressional_bills' && getMostRecentActionDate(actions) 
              ? getMostRecentActionDate(actions) 
              : date}
          </Card.Body>

          {/* Type for awards/savings */}
          {((table === 'recent_awards' || table === 'doge_savings')) && type && (
            <Card.Body id='billBodyText'>{type}</Card.Body>
          )}

          {/* Full Description */}
          {description && table !== 'x_account_1856751787644260354_posts' && (
            <div className="mt-3">
              <Card.Body id="billBodyText">{description}</Card.Body>
            </div>
          )}

          {/* Award Amount */}
          {((table === 'recent_awards' || table === 'doge_savings') && awardAmount
            && awardAmount != '0' && awardAmount !== '' && Number(awardAmount) !== 0) && (
            <Card.Body id='billBodyText'>
              Total value: ${Number(awardAmount).toLocaleString()}
            </Card.Body>
          )}

          {/* Saved Amount */}
          {((table === 'recent_awards' || table === 'doge_savings') && 
            savedAmount && savedAmount !== '0' && savedAmount !== '' && parseFloat(savedAmount) > 0) && (
            <Card.Body id='billBodyText'>
              Savings: ${parseFloat(savedAmount).toLocaleString()}
            </Card.Body>
          )}

          {/* Congressional Bills specific sections */}
          {(table === 'congressional_bills' || table === 'congressional_bills_enrolled' || table === 'public_and_private_laws') && (
            <>
              {/* Actions */}
              {actions && actions.length > 0 && (
                <div className="mt-3">
                  <Card.Title id="billText">Actions:</Card.Title>
                  {actions.map((action, index) => (
                    action.action_description ? (
                      <Card.Body id="billBodyText" key={index}>
                        <strong>{formatDate(action.action_date)}</strong> - {action.action_description}
                      </Card.Body>
                    ) : null
                  ))}
                </div>
              )}

              {/* Committees */}
              {committees && committees.length > 0 && (
                <div className="mt-3">
                  {committees.map((committee, index) => (
                    <Card.Body id="billBodyText" key={index}>
                      {committee.committeeName} ({committee.chamber})
                    </Card.Body>
                  ))}
                </div>
              )}

              {/* Sponsors and Cosponsors */}
              {renderSponsorsAndCosponsors()}
            </>
          )}

          {/* Member Votes for all_member_votes */}
          {renderMemberVotes()}

          {/* Action Buttons */}
          <div className="action-buttons-container">
            {link && (
              <Button
                id="billLink"
                onClick={() => openCapacitorSite(link)}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="lucide lucide-info-icon lucide-info"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4"/><path d="M12 8h.01"/></svg> Source
              </Button>
            )}

            {docLink && (
              <Button
                id="viewBillButton"
                onClick={() => openCapacitorSite(docLink)}
              >
                <FileText /> {buttonName}
              </Button>
            )}

            {/* Use the CollectionButton from the hook */}
            <CollectionButton 
              item={{
                title: titleToDisplay,
                url: item.url,
                docLink: docLink || '',
                date: item.created_at,
                feedName: formatTableName(table),
                branch: '',
                topic: ''
              }}
              className=""
            />

            <Button
              id="shareButton"
              onClick={handleShareClick}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="16"
                height="16"
                fill="currentColor"
                className="bi bi-send"
                viewBox="0 0 16 16"
              >
                <path d="M15.854.146a.5.5 0 0 1 .11.54l-5.819 14.547a.75.75 0 0 1-1.329.124l-3.178-4.995L.643 7.184a.75.75 0 0 1 .124-1.33L15.314.037a.5.5 0 0 1 .54.11ZM6.636 10.07l2.761 4.338L14.13 2.576zm6.787-8.201L1.591 6.602l4.339 2.76z" />
              </svg>
            </Button>
          </div>
        </Container>
      </div>

      {/* Collection Modal - from useCollections hook */}
      <CollectionModal
        isModalOpen={isModalOpen}
        currentItem={currentItem}
        saveSuccess={saveSuccess}
        note={note}
        setNote={setNote}
        handleSave={handleSave}
        closeModal={closeModal}
        isStorageAvailable={isStorageAvailable}
      />

      {/* Share Modal */}
      <Modal
        show={shareModalIsOpen}
        size="xl"
        centered
        onHide={() => setShareModalIsOpen(false)}
        className="share-modal"
      >
        <Modal.Header closeButton className="share-modal-header">
          <h3>Share</h3>
        </Modal.Header>
        <Modal.Body className="share-modal-body">
          <Form.Group>
            <Form.Control
              type="text"
              readOnly
              value={shareLink}
              className="share-link-input"
            />
          </Form.Group>
          <Modal.Footer className="share-modal-footer">
            <Button
              onClick={handleCopyLink}
              className="copy-link-button"
            >
              {linkCopied ? <ClipboardCheck /> : <Clipboard />}
            </Button>
          </Modal.Footer>
        </Modal.Body>
      </Modal>

      <style jsx>{`
        /* Page Layout */
        .detail-page-container {
          min-height: 100vh;
          padding: 20px;
          background-color: var(--bg-primary);
        }

        .detail-content-wrapper {
          display: flex;
          align-items: center;
          justify-content: center;
        }

        .detail-loading-container {
          min-height: 100vh;
          display: flex;
          justify-content: center;
          align-items: center;
          background-color: var(--bg-primary);
        }

        /* Main Container */
        #feedContainerNormalView {
          padding: 20px;
          border-radius: 18px;
          margin-top: 0em;
          max-width: 860px;
          width: 100%;
          background-color: var(--bg-card);
          box-shadow: 10px 10px 10px var(--shadow-color);
        }

        /* Text Styles */
        #billText {
          font-size: 16px;
          font-family: var(--site-font);
          color: var(--text-primary);
        }

        #billTextMore {
          font-size: 20px;
          font-family: var(--site-font);
          color: var(--text-primary);
        }

        #billBodyText {
          font-size: 14px;
          font-family: var(--site-font);
          padding-top: 0px;
          padding-bottom: 10px;
          color: var(--text-primary);
        }

        /* Sponsor and voter photos: small, so a roll call's 435 rows read as a
           list of names rather than a wall of faces. The container is pinned
           to the photo's size so no shared rule can stretch it. */
        #profileImageSponsors {
          flex: 0 0 32px;
          width: 32px;
          height: 32px;
        }

        /* Sponsor/Vote Text */
        .sponsor-text,
        .vote-text {
          margin-bottom: 0;
          color: var(--text-primary);
          display: flex;
          align-items: center;
        }

        /* Action Buttons Container */
        .action-buttons-container {
          display: flex;
          flex-wrap: wrap;
          gap: 10px;
          margin-top: 20px;
        }

        /* Button Styles */
        #billLink {
          padding-top: 5px;
          padding-bottom: 5px;
          color: var(--text-primary);
          border: 0px;
          box-shadow: 2px 2px 12px var(--shadow-color);
          background-color: var(--bg-button);
          border-radius: 20px;
        }

        #billLink:hover {
          background-color: var(--bg-button-hover);
        }

        #viewBillButton {
          padding-top: 5px;
          padding-bottom: 5px;
          color: var(--text-primary);
          background-color: var(--bg-button);
          border-radius: 20px;
          border: 0px;
          box-shadow: 2px 2px 12px var(--shadow-color);
        }

        #viewBillButton:hover {
          background-color: var(--bg-button-hover);
        }

        #addToCollectionButton {
          padding-top: 5px;
          padding-bottom: 5px;
          color: var(--text-primary);
          border: 0px;
          background-color: var(--bg-button);
          border-radius: 20px;
          box-shadow: 2px 2px 12px var(--shadow-color);
        }

        #addToCollectionButton:hover {
          background-color: var(--bg-button-hover);
        }

        #shareButton {
          padding-top: 5px;
          padding-bottom: 5px;
          color: var(--text-primary);
          border: 0px;
          background-color: var(--bg-button);
          border-radius: 20px;
          box-shadow: 2px 2px 12px var(--shadow-color);
        }

        #shareButton:hover {
          background-color: var(--bg-button-hover);
        }

        /* Sponsor/Vote Item Hover */
        .sponsorItem:hover,
        .voteItem:hover {
          background-color: var(--bg-card-hover);
          border-radius: 10px;
          padding: 5px;
          margin-left: -5px;
        }

        /* Share Modal Styles */
        .share-modal .modal-content {
          background-color: var(--modal-bg);
          border: 1px solid var(--border-primary);
        }

        .share-modal-header {
          background-color: var(--modal-header-bg);
          color: var(--text-primary);
          border: none;
          border-bottom: 1px solid var(--border-secondary);
        }

        .share-modal-header .btn-close {
          filter: var(--modal-close-filter, invert(1));
        }

        .share-modal-body {
          background-color: var(--modal-bg);
          color: var(--text-primary);
        }

        .share-modal-footer {
          background-color: var(--modal-bg);
          border: none;
        }

        .share-link-input {
          background-color: var(--input-bg);
          color: var(--text-primary);
          border: 1px solid var(--border-secondary);
        }

        .share-link-input:focus {
          background-color: var(--input-bg);
          color: var(--text-primary);
          border-color: var(--accent-primary);
          box-shadow: 0 0 0 2px var(--accent-glow);
        }

        .copy-link-button {
          background-color: var(--bg-button);
          border: none;
          color: var(--text-primary);
        }

        .copy-link-button:hover {
          background-color: var(--bg-button-hover);
        }

        /* ===========================================
           DARK THEME SPECIFIC OVERRIDES (True Black/OLED)
           =========================================== */

        [data-theme="dark"] .detail-page-container,
        [data-theme="dark"] .detail-loading-container {
          background-color: #000000;
        }

        [data-theme="dark"] #feedContainerNormalView {
          background-color: rgba(10, 10, 10, 0.9);
          box-shadow: 10px 10px 10px rgba(0, 0, 0, 0.8);
          border: 1px solid rgba(255, 255, 255, 0.06);
        }

        [data-theme="dark"] #billText,
        [data-theme="dark"] #billTextMore,
        [data-theme="dark"] #billBodyText,
        [data-theme="dark"] .sponsor-text,
        [data-theme="dark"] .vote-text {
          color: #ffffff;
        }

        [data-theme="dark"] #billLink,
        [data-theme="dark"] #viewBillButton,
        [data-theme="dark"] #addToCollectionButton,
        [data-theme="dark"] #shareButton {
          background-color: #0a0a0a;
          color: #ffffff;
          box-shadow: 2px 2px 12px rgba(0, 0, 0, 0.8);
          border: 1px solid rgba(255, 255, 255, 0.08);
        }

        [data-theme="dark"] #billLink:hover,
        [data-theme="dark"] #viewBillButton:hover,
        [data-theme="dark"] #addToCollectionButton:hover,
        [data-theme="dark"] #shareButton:hover {
          background-color: #141414;
        }

        [data-theme="dark"] .sponsorItem:hover,
        [data-theme="dark"] .voteItem:hover {
          background-color: rgba(20, 20, 20, 0.8);
        }

        [data-theme="dark"] .share-modal .modal-content {
          background-color: #000000;
          border: 1px solid rgba(255, 255, 255, 0.08);
        }

        [data-theme="dark"] .share-modal-header {
          background-color: #000000;
          color: #ffffff;
          border-bottom: 1px solid rgba(255, 255, 255, 0.08);
        }

        [data-theme="dark"] .share-modal-body {
          background-color: #000000;
          color: #ffffff;
        }

        [data-theme="dark"] .share-link-input {
          background-color: rgba(10, 10, 10, 0.9);
          color: #ffffff;
          border: 1px solid rgba(255, 255, 255, 0.08);
        }

        [data-theme="dark"] .copy-link-button {
          background-color: #0a0a0a;
          color: #ffffff;
          border: 1px solid rgba(255, 255, 255, 0.08);
        }

        [data-theme="dark"] .copy-link-button:hover {
          background-color: #141414;
        }

        /* ===========================================
           LIGHT THEME SPECIFIC OVERRIDES
           =========================================== */

        [data-theme="light"] .detail-page-container,
        [data-theme="light"] .detail-loading-container {
          background-color: #ffffff;
        }

        [data-theme="light"] #feedContainerNormalView {
          background-color: #f0f2f5;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.12);
        }

        [data-theme="light"] #billText,
        [data-theme="light"] #billTextMore,
        [data-theme="light"] #billBodyText,
        [data-theme="light"] .sponsor-text,
        [data-theme="light"] .vote-text {
          color: #000000;
        }

        [data-theme="light"] #billLink,
        [data-theme="light"] #viewBillButton,
        [data-theme="light"] #addToCollectionButton,
        [data-theme="light"] #shareButton {
          background-color: #e9ecef;
          color: #000000 !important;
          box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
        }

        [data-theme="light"] #billLink:hover,
        [data-theme="light"] #viewBillButton:hover,
        [data-theme="light"] #addToCollectionButton:hover,
        [data-theme="light"] #shareButton:hover {
          background-color: #dee2e6;
        }

        [data-theme="light"] .sponsorItem:hover,
        [data-theme="light"] .voteItem:hover {
          background-color: #e5e7eb;
        }

        [data-theme="light"] .share-modal .modal-content {
          background-color: #ffffff;
          border: 1px solid #dee2e6;
        }

        [data-theme="light"] .share-modal-header {
          background-color: #f0f2f5;
          color: #000000;
          border-bottom: 1px solid #dee2e6;
        }

        [data-theme="light"] .share-modal-header .btn-close {
          filter: none;
        }

        [data-theme="light"] .share-modal-body {
          background-color: #ffffff;
          color: #000000;
        }

        [data-theme="light"] .share-link-input {
          background-color: #f8f9fa;
          color: #000000;
          border: 1px solid #dee2e6;
        }

        [data-theme="light"] .share-link-input:focus {
          background-color: #ffffff;
          color: #000000;
        }

        [data-theme="light"] .copy-link-button {
          background-color: #e9ecef;
          color: #000000;
        }

        [data-theme="light"] .copy-link-button:hover {
          background-color: #dee2e6;
        }
      `}</style>
    </div>
  );
};

export default FeedItemDetailContent;